<template>
  <v-modal
    id="admin-user-agencies-edit"
    class-name="modal-fullscreen-sm-down"
    size="lg"
    @closed="$emit('closed')"
  >
    <modal-header>
      <modal-title>
        {{ $t('admin.user.addAgencies') }}
      </modal-title>
    </modal-header>

    <modal-body>
      <v-select-agency-search
        id="user-agencies-search"
        :selected-options="selectedAgencies"
        :user-aux="user"
        @agency-attached="reloadUser"
      />

      <div class="search-separator-container mb-16 pb-36">
        <div class="separator-text-container">
          <span class="separator-text text-sm">{{ $t('general.shared.orCreateNew') }}</span>
        </div>
      </div>

      <div class="agency-create-container">
        <the-admin-user-create-agency-address
          :user-id="user.id"
          @agency-created="reloadUser"
        />
      </div>
    </modal-body>
  </v-modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import VModal from '@/components/vendor/basic/modal/VModal.vue';
import ModalHeader from '@/components/vendor/basic/modal/ModalHeader.vue';
import ModalTitle from '@/components/vendor/basic/modal/ModalTitle.vue';
import ModalBody from '@/components/vendor/basic/modal/ModalBody.vue';
import api from '@/api';
import Agency from '@/api/objects/Agency';
import VSelectAgencySearch from '@/components/vendor/selectSearch/VSelectAgencySearch.vue';
import AgencyUser from '@/api/objects/AgencyUser';
import TheAdminUserCreateAgencyAddress from '@/components/admin/user/parts/TheAdminUserCreateAgencyAddress.vue';
import User from '@/api/objects/User';

export default defineComponent({
  name: 'TheAdminUserAddAgenciesModal',
  components: {
    TheAdminUserCreateAgencyAddress,
    VSelectAgencySearch,
    ModalBody,
    ModalTitle,
    ModalHeader,
    VModal,
  },
  emits: [
    'closed',
    'emitReloadUser',
  ],
  props: {
    user: {
      type: User,
      required: true,
    },
  },
  data() {
    return {
      disableUnassignAgencyUserBtn: false as boolean,
    };
  },
  computed: {
    selectedAgencies() {
      const agencyUsers = this.user.agencyUsers?.map((agencyUser: any) => agencyUser);

      if (!!agencyUsers) {
        return agencyUsers.map((agencyUser: AgencyUser) => agencyUser.agency);
      }

      return [];
    },
  },
  methods: {
    async unassignAgencyUser(agency: Agency) {
      await this.$modal.delete({
        title: this.$t('admin.user.removeAgencyUserTitle'),
        text: this.$t('admin.user.removeAgencyUserText', {
          user: this.user?.name,
          agency: agency.name,
          email: this.user?.email,
        }),
        deleteButtonText: this.$t('general.button.remove'),
        deleteButtonLoadingText: this.$t('general.button.removing'),
        rightButtonClasses: 'btn-tertiary',
        deleteButtonCallback: () => this.doUnassignAgencyUser(agency.id),
      });
    },
    async doUnassignAgencyUser(agencyId: string | number) {
      try {
        this.disableUnassignAgencyUserBtn = true;

        await api.admin.agency.attachUser(agencyId, this.user.id);

        this.$toast.success(this.$t('general.shared.savedChanges'));

        await this.reloadUser();

        this.disableUnassignAgencyUserBtn = false;
      } catch (e: any) {
        this.disableUnassignAgencyUserBtn = false;

        console.error(e.response.data);

        this.$toast.error(e.response.data.message);
      }
    },
    async reloadUser() {
      this.$emit('emitReloadUser');
    },
  },
});
</script>
